import React, {useMemo} from 'react';
import {CheckmarkFilled, CircleDash} from '@carbon/icons-react';
import './comparison-empty-state.scss';
import {ComparisonEmptyStateProps, Requirement} from './types';

// Glyphs match the MPDS status set: a filled check once met, a dashed ring while
// outstanding. Decorative only (Carbon icons are aria-hidden without a label) -
// the sr-only prefix on each label states the row's state.
const StatusIcon = React.memo<{met: boolean}>(({met}) => (
    met ? (
        <CheckmarkFilled
            size={16}
            className='comparison-empty-state__icon comparison-empty-state__icon--complete'
            data-testid='status-complete'
        />
    ) : (
        <CircleDash
            size={16}
            className='comparison-empty-state__icon comparison-empty-state__icon--incomplete'
            data-testid='status-incomplete'
        />
    )
));

StatusIcon.displayName = 'StatusIcon';

// One instruction per unmet requirement, joined as "a, b and c"
const buildGuidance = (requirements: Requirement[]): string => {
    const todo: string[] = [];
    requirements.forEach((req) => {
        if (req.met) return;
        if (req.id === 'primary-roster') {
            todo.push('select a primary roster above');
        } else if (req.id === 'minimum-rosters') {
            const remaining = req.remaining ?? 1;
            // Nothing selected at all: ask for a comparison roster rather than "add 2 more"
            todo.push(remaining >= 2
                ? 'add at least one comparison roster in Filters'
                : `add ${remaining} more in Filters`);
        } else if (req.id === 'county-selected') {
            todo.push('select a county in Filters');
        } else if (req.id === 'zip-selected') {
            todo.push('select a ZIP code in Filters');
        }
    });

    if (todo.length === 0) return '';

    const where = todo.length > 1
        ? `${todo.slice(0, -1).join(', ')} and ${todo[todo.length - 1]}`
        : todo[0];

    return 'Network adequacy is compared between rosters and measured per county. '
        + `${where.charAt(0).toUpperCase()}${where.slice(1)}.`;
};

// Renders no live region: the consumer announces state changes with useScreenReaderAnnouncement,
// so a second region here would read each state out twice
const ComparisonEmptyState: React.FC<ComparisonEmptyStateProps> = ({
    variant,
    requirements = [],
    errorMessage,
    completedCount = 0,
    totalCount = 0,
}) => {
    const guidanceText = useMemo(() => buildGuidance(requirements), [requirements]);

    switch (variant) {
    case 'requirements-checklist':
        return (
            <div className='comparison-empty-state'>
                <div className='comparison-empty-state__checklist'>
                    <p className='comparison-empty-state__title'>Required selections</p>
                    <p className='comparison-empty-state__progress'>
                        {completedCount} of {totalCount} complete
                    </p>
                    <ul className='comparison-empty-state__requirements'>
                        {requirements.map((req) => (
                            <li
                                key={req.id}
                                className='comparison-empty-state__requirement'
                                data-testid={`requirement-${req.id}`}
                                data-met={req.met}
                            >
                                <StatusIcon met={req.met} />
                                <span className='comparison-empty-state__requirement-label'>
                                    <span className='sr-only'>{req.met ? 'Complete: ' : 'Required: '}</span>
                                    {req.label}
                                </span>
                                <span className='comparison-empty-state__requirement-message'>
                                    {req.message}
                                </span>
                            </li>
                        ))}
                    </ul>
                    {guidanceText && (
                        <p className='comparison-empty-state__guidance'>{guidanceText}</p>
                    )}
                </div>
            </div>
        );
    case 'no-rosters':
        return (
            <div className='comparison-empty-state'>
                <p className='comparison-empty-state__title'>No rosters available</p>
                <p className='comparison-empty-state__message'>
                    No rosters have been uploaded for this client yet. Add one in Roster, then come back to compare.
                </p>
            </div>
        );
    case 'error':
        return (
            <div className='comparison-empty-state'>
                <p className='comparison-empty-state__title'>Couldn&rsquo;t load comparison</p>
                <p className='comparison-empty-state__message'>
                    {errorMessage || 'The roster comparison data didn’t load. Please try again.'}
                </p>
            </div>
        );
    default:
        return null;
    }
};

export default React.memo(ComparisonEmptyState);
